import {useId} from 'react'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import type {Icon} from '@primer/octicons-react'
import {Image, Link, MinimalVideoPlayer, RiverBreakoutTabs, Text} from '@primer/react-brand/esm'

import {getGenericContentLink, type GenericContent} from '../../../../../brand/lib/types/contentful'

type Props = {
  items: GenericContent[]
  heading?: string
}

export function HeroCarousel({items, heading}: Props) {
  // eslint-disable-next-line @eslint-react/naming-convention-id-name
  const descriptionIdPrefix = useId()
  const accessibleHeading = heading ?? items.at(0)?.fields.heading

  if (!accessibleHeading || items.length === 0) return null

  return (
    <RiverBreakoutTabs
      backgroundVisual={
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'var(--base-color-scale-green-0)',
            backgroundImage: 'radial-gradient(var(--base-color-scale-green-2) 1px, transparent 3px)',
            backgroundSize: '10px 10px',
          }}
        />
      }
      imagePosition="block-end"
      backgroundVisualFullBleed
    >
      <RiverBreakoutTabs.A11yHeading as="h2">{accessibleHeading}</RiverBreakoutTabs.A11yHeading>

      {items.map(item => {
        const {label, heading: itemHeading, media} = item.fields
        const icon = getPrimerIcon(label?.fields.icon) as Icon
        const link = getGenericContentLink(item)
        const mediaItem = media?.at(0)?.fields
        const mediaFile = mediaItem?.asset.fields.file
        const imageDimensions = mediaFile?.details?.image
        const description = mediaItem?.description ?? mediaItem?.asset.fields.description
        const descriptionId = description ? `${descriptionIdPrefix}-${item.sys.id}` : undefined

        return (
          <RiverBreakoutTabs.Item key={item.sys.id}>
            {icon ? <RiverBreakoutTabs.Icon icon={icon} /> : null}
            {itemHeading ? <RiverBreakoutTabs.Heading>{itemHeading}</RiverBreakoutTabs.Heading> : null}

            <RiverBreakoutTabs.Content>
              {link ? (
                <Link
                  href={link.fields.href}
                  {...getAnalyticsEvent({
                    action: link.fields.text,
                    tag: 'button',
                    context: 'CTAs',
                    location: 'hero carousel',
                  })}
                >
                  {link.fields.text}
                </Link>
              ) : null}
            </RiverBreakoutTabs.Content>

            {mediaFile ? (
              <RiverBreakoutTabs.Visual>
                {imageDimensions ? (
                  <Image
                    src={mediaFile.url}
                    width={mediaItem?.width ?? imageDimensions.width}
                    height={mediaItem?.height ?? imageDimensions.height}
                    alt={description ?? ''}
                  />
                ) : (
                  <>
                    {description ? (
                      <Text className="sr-only" id={descriptionId}>
                        {description}
                      </Text>
                    ) : null}
                    <MinimalVideoPlayer
                      width={mediaItem?.width}
                      height={mediaItem?.height}
                      title={itemHeading ?? t('Video')}
                      aria-describedby={descriptionId}
                    >
                      <source src={mediaFile.url} />
                    </MinimalVideoPlayer>
                  </>
                )}
              </RiverBreakoutTabs.Visual>
            ) : null}
          </RiverBreakoutTabs.Item>
        )
      })}
    </RiverBreakoutTabs>
  )
}
